import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { CustomisedTertiaryButton, TextWrapper, Wrapper, TextWrapperSpan } from "../../../constants/common/StyledGlobalConstants";
import { Line } from "@vds/lines";
import {
  setLoader,
  fetchAccountDetails,
  fetchRevealVehicles,
  setServiceDetails,
  setUpdateJob,
  validateVin,
  checkVinCompatibility,
  getVinControlUnits,
  setIsAssistanceRequired,
  setAccessoriesLoader,
} from "../../../store/actions/schedulingportal/technicianAppointment";
import CurrentVehicleSearch from "./CurrentVehicleSearch";
import CamerasAndOtherAccessories from "./CamerasAndOtherAccessories";
import NewVehicleorAsset from "./NewVehicleorAsset";
import SaveService from "./SaveService";
import ContractAndCancellationsFAQWarning from "../common/ContractAndCancellationsFAQWarning";
import { serviceDetailsObject } from "../common/JobsPayload";
import styled from "styled-components";
import TryAgain from "./TryAgain";
import CPNotification from "../../../pages/Login/CPNotification";
import { WARNING_REASON_DISPLAY_MESSAGES } from "../../../constants/technicianAppointmentConstants";
import BLOCKING_ERROR_NOTIFICATIONS from "./BlockingErrors";
import "../../../constants/common/StyleOnlyForScreenReader.css";
import { getPendoId } from "../../../util/cfstPendoUtils";
import { CFST_PENDO_DEFAULT_LABEL, pendoTrack } from "../../../util/pendoUtil";

const ServiceTypeContainer = styled(Wrapper)`
  margin-top: 27px !important;
  @media (max-width: 480px) {
    margin-top: 22px !important;
  }
`;

const ServiceTypeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  @media (max-width: 480px) {
    flex-direction: ${(props) => (props.close ? "column" : "row")};
  }
  @media (max-width: 380px) {
    flex-direction: column;
  }
`;

const VehicleAssetDetails = ({
  serviceType,
  setShowSnackBar,
  isAdditionalService,
  setIsAdditionalService,
  stepperRef,
  isEditVehicleAssetDetails,
  setIsEditVehicleAssetDetails,
  setIsDiscardChange,
  isNextDisabled,
  setIsNextDisabled,
  setIsAddAnotherService,
  isAddAnotherService,
  serviceCount,
  jobId,
  isFieldsEdited,
  setIsFieldsEdited,
  setIsFirstServiceEdited,
}) => {
  const [selectedVinOrPA, setSelectedVinOrPA] = useState(null);
  const [selectedAccessories, setSelectedAccessories] = useState(null);
  const [highlightAccessories, setHighlightAccessories] = useState(false);
  const [vin, setVin] = useState({
    isValid: true,
    errorText: "",
    value: "",
  });
  const [newVehicle, setNewVehicle] = useState({
    isValid: true,
    errorText: "",
    value: "",
  });
  const [isSaveServiceDisabled, setIsSaveServiceDisabled] = useState(true);
  const [isServiceFailure, setIsServiceFailure] = useState({
    vinValidationFailed: false,
    hwsCompatibilityFailed: false,
    vinFeaturesError: false,
    vinControlUnitsError: false,
  });
  const [currentServiceDetails, setCurrentServiceDetails] = useState(null);
  const [blockingError, setBlockingError] = useState({ isError: false, errorReason: null });

  const {
    serviceDetails,
    updateJob,
    accountDetails: { error: sfdcAccountDetailsError },
    vehicleAndAssetList: { error: revealVehiclesError },
  } = useSelector((state) => state.TECHNICIANAPPOINTMENT);
  const { account_id, accountNumber, isSourcewellGovernment } = useSelector((state) => state.ACCOUNT);
  const dispatch = useDispatch();

  useEffect(() => {
    //Populate the vehicle details to edit
    if (isEditVehicleAssetDetails.isOpen) {
      let serviceDetailsList = serviceDetails[isEditVehicleAssetDetails.index],
        serviceAccessories = serviceDetailsList.selectedAccessories ? [...serviceDetailsList.selectedAccessories] : null;
      setVin((prevState) => ({
        ...prevState,
        isValid: !serviceDetailsList.isVinInvalid,
        value: serviceDetailsList.vin,
        errorText: serviceDetailsList.isVinInvalid ? "VIN is invalid" : "",
      }));
      setNewVehicle((prevState) => ({ ...prevState, value: serviceDetailsList.newVehicle, isValid: true }));
      setSelectedVinOrPA(serviceDetailsList.selectedVinOrPA);
      setSelectedAccessories(serviceAccessories);
      setCurrentServiceDetails(serviceDetailsList);
    } else {
      if (serviceDetails[isEditVehicleAssetDetails.index] !== null) {
        setIsEditVehicleAssetDetails({
          isOpen: false,
          index: -1,
        });
      }
      setCurrentServiceDetails(null);
      setIsFieldsEdited(false);
    }
  }, [isEditVehicleAssetDetails.isOpen]);

  useEffect(() => {
    let isVinAndPAAndAccessoriesSelected =
        !!selectedVinOrPA && (selectedVinOrPA.isVin ? (selectedVinOrPA.vin || selectedVinOrPA.newVin) && !!selectedAccessories : true),
      isNewVehicleSpecified = true;
    if (serviceType === 1) {
      let isNewVehicleNameValid = newVehicle.isValid && newVehicle.value;
      isNewVehicleSpecified =
        (selectedVinOrPA?.isVin ? vin.value && (vin.isValid || vin.errorText === "VIN is invalid") : true) && isNewVehicleNameValid;
    }
    let saveServiceDisable = !(isVinAndPAAndAccessoriesSelected && isNewVehicleSpecified) || blockingError.isError,
      nextButtonDisable = true,
      isAnyFieldEdited = selectedVinOrPA || selectedAccessories || vin.value || newVehicle.value;
    if (isEditVehicleAssetDetails.isOpen) {
      // Enable the SaveService button and disable the Next button until the edited fields are saved
      let serviceDetailsList = serviceDetails[isEditVehicleAssetDetails.index];
      let isEdit =
        JSON.stringify(serviceDetailsList.newVehicle) !== JSON.stringify(newVehicle.value) ||
        JSON.stringify(serviceDetailsList.vin) !== JSON.stringify(vin.value) ||
        JSON.stringify(serviceDetailsList.selectedVinOrPA) !== JSON.stringify(selectedVinOrPA) ||
        JSON.stringify(serviceDetailsList.selectedAccessories?.sort()) !== JSON.stringify(selectedAccessories?.sort());
      isAnyFieldEdited = isEdit;
      saveServiceDisable = saveServiceDisable || !isEdit;
      nextButtonDisable = isEdit || serviceDetails.some((service) => service.isError);
    }
    setIsFieldsEdited(isAnyFieldEdited);
    setIsSaveServiceDisabled(saveServiceDisable);
    setIsNextDisabled(nextButtonDisable);
    serviceDetails.length === 0 && setIsFirstServiceEdited(isAnyFieldEdited);
  }, [selectedVinOrPA, selectedAccessories, vin, newVehicle]);

  const handleClose = () => {
    // Display Discard modal if the changes are not saved in AddNewService
    if (!isAddAnotherService) {
      if (isFieldsEdited) setIsDiscardChange({ isOpen: true, toEditIndex: -1, toDeleteIndex: -1, toAddNewService: false });
      else {
        setIsAddAnotherService(true);
        setIsNextDisabled(false);
      }
    }

    // Display Discard modal if the edited changes are not saved
    isEditVehicleAssetDetails.isOpen && isFieldsEdited
      ? setIsDiscardChange({ isOpen: true, toEditIndex: "close", toDeleteIndex: -1, toAddNewService: false })
      : setIsEditVehicleAssetDetails({
          isOpen: false,
          index: -1,
        });
  };
  // Helper function to validate selected value and determine blocking errors
  const validateSelectedValue = (selectedValue) => {
    if (!selectedValue) {
      return { blockingError: false, blockingErrorReason: null };
    }

    if (selectedValue.isOEM) {
      return {
        blockingError: true,
        blockingErrorReason: isSourcewellGovernment ? "OEM_DETECTED_GOVT" : "OEM_DETECTED",
      };
    }

    if (!selectedValue.isVin && !selectedValue.isPoweredAsset) {
      return {
        blockingError: true,
        blockingErrorReason: "NON_POWERED_ASSET_DETECTED",
      };
    }

    if (!selectedValue.lastPingDate) {
      return {
        blockingError: true,
        blockingErrorReason: "DEVICE_NOT_REPORTED",
      };
    }

    const isDuplicate = serviceDetails.some((service) => service.selectedVinOrPA.vehicleId === selectedValue.vehicleId);
    return {
      blockingError: isDuplicate,
      blockingErrorReason: isDuplicate ? "DUPLICATE_VEHICLE_ASSET_DETECTED" : "",
    };
  };

  // Helper function to clear form states
  const clearFormStates = () => {
    setSelectedAccessories(null); // clearing out selected accessories when selecting a VIN or Powered Asset
    setHighlightAccessories(false); // not highlighting the accessories section when selecting a VIN or Powered Asset
    setVin((prevState) => ({ ...prevState, isValid: true, errorText: "", value: "" })); // clearing out new vin details if powered asset is selected
    setNewVehicle((prevState) => ({ ...prevState, isValid: true, errorText: "", value: "" })); // clearing out new vehicle name validations if any vehicle or powered asset is selected
  };

  // Helper function to handle device type fetching
  const handleDeviceTypeFetch = (selectedValue, blockingError) => {
    if (selectedValue?.isVin && selectedValue?.vin && !blockingError) {
      serviceType === 1 ? getDeviceType(selectedValue) : getVehicleControlUnits(selectedValue, true);
    }
  };

  //method to set the selected VIN or Powered Asset
  const searchHandler = (selectedValue) => {
    const { blockingError, blockingErrorReason } = validateSelectedValue(selectedValue);

    if (blockingError) {
      pendoTrack(`${CFST_PENDO_DEFAULT_LABEL} | Vehicle Asset details page | Notification`, {
        status: blockingErrorReason,
        accountId: accountNumber,
      });
    }

    setBlockingError({ isError: blockingError, errorReason: blockingErrorReason });
    setSelectedVinOrPA(selectedValue);
    clearFormStates();
    handleDeviceTypeFetch(selectedValue, blockingError);
  };

  const checkAccessoriesSection = () => {
    setHighlightAccessories(!selectedAccessories?.length);
  };

  const handleLandingPageAPI = () => {
    if (sfdcAccountDetailsError) {
      dispatch(fetchAccountDetails(account_id));
    }
    if (revealVehiclesError) {
      dispatch(fetchRevealVehicles());
    }
    if (isServiceFailure.vinFeaturesError) {
      getDeviceType(selectedVinOrPA);
    }
    if (isServiceFailure.vinControlUnitsError) {
      getVehicleControlUnits(selectedVinOrPA, true);
    }
  };

  //function to call if HWS API fails
  const handleTryAgain = () => {
    if (isServiceFailure.vinValidationFailed) {
      handleVinValidation(false);
    } else {
      handleVinCompatibility(currentServiceDetails?.year, currentServiceDetails?.make, currentServiceDetails?.model, true, false);
    }
  };

  //function to fetch the boxtype from SL
  const getDeviceType = (selectedValue) => {
    const selectedVehicle = {
      ...selectedValue,
      isBoxNameTypeCompatible: !!selectedValue.boxName,
      boxModelFetchedVin: selectedValue.vin || selectedValue.newVin,
    };
    setSelectedVinOrPA(selectedVehicle);
    selectedVehicle.esn !== selectedVehicle.camerasFetchedEsn ? getVehicleControlUnits(selectedVehicle, true) : dispatch(setAccessoriesLoader(false));
  };

  //function to fetch the cameras for the selected vehicle
  const getVehicleControlUnits = (selectedValue, toLoad = false) => {
    const callBack = (cameras = [], noCameraReason, apiFailed = false) => {
      const selectedVehicle = { ...selectedValue, cameras, noCameraReason, camerasFetchedEsn: apiFailed ? "" : selectedValue.esn };
      setSelectedVinOrPA(selectedVehicle);
      if (apiFailed) {
        setIsServiceFailure((prev) => ({
          ...prev,
          vinControlUnitsError: true,
        }));
      } else {
        isServiceFailure.vinControlUnitsError &&
          setIsServiceFailure((prev) => ({
            ...prev,
            vinControlUnitsError: false,
          }));
        noCameraReason === "EVC_DETECTED" &&
          setBlockingError({ isError: true, errorReason: isSourcewellGovernment ? "EVC_DETECTED_GOVT" : "EVC_DETECTED" });
      }
    };
    dispatch(getVinControlUnits(selectedValue.esn, callBack, toLoad));
  };

  const BOXNAME_TYPE_MISMATCH_ERROR = "REVEAL_BOXNAME_AND_SL_BOXTYPE_DID_NOT_MATCH";
  const SERVICE_TYPE_VIN = 1;

  // Helper function for saveService
  const saveServiceHelper = (vinOrPA, vehicleData, validationStatus) => {
    const { year, make, model, validatedVin } = vehicleData;
    const { isCompatible, isError, errorReason, isWarning, warningReason } = validationStatus;
    saveService(vinOrPA, year, make, model, validatedVin, isCompatible, isError, errorReason, isWarning, warningReason);
  };

  // Helper function to handle saving service details
  const saveServiceDetails = (vinOrPA, currentServiceDetails, errorReason, isVinChanged, isBoxNameTypeCompatible) => {
    if (isBoxNameTypeCompatible) {
      if (isVinChanged) {
        handleVinValidation();
      } else if (
        (JSON.stringify(currentServiceDetails?.selectedAccessories?.sort()) !== JSON.stringify(selectedAccessories?.sort()) ||
          currentServiceDetails?.selectedVinOrPA.boxModelFetchedVin !== selectedVinOrPA.boxModelFetchedVin ||
          currentServiceDetails?.selectedVinOrPA.boxName !== selectedVinOrPA.boxName) &&
        !currentServiceDetails?.isVinInvalid
      ) {
        handleVinCompatibility(currentServiceDetails.year, currentServiceDetails.make, currentServiceDetails.model, true);
      } else {
        saveServiceHelper(
          currentServiceDetails?.selectedVehicleOrPA?.value !== selectedVinOrPA.value ? selectedVinOrPA : currentServiceDetails?.selectedVinOrPA,
          {
            year: currentServiceDetails.year,
            make: currentServiceDetails.make,
            model: currentServiceDetails.model,
            validatedVin: currentServiceDetails?.validatedVin,
          },
          {
            isCompatible: currentServiceDetails.isCompatible,
            isError: currentServiceDetails.isError,
            errorReason: currentServiceDetails.errorReason,
            isWarning: currentServiceDetails.isWarning,
            warningReason: currentServiceDetails.warningReason,
          }
        );
      }
    } else if (isVinChanged) {
        saveServiceHelper(
          vinOrPA,
          { year: null, make: null, model: null, validatedVin: "" },
          { isCompatible: false, isError: false, errorReason: null, isWarning: true, warningReason: errorReason }
        );
      } else {
        saveServiceHelper(
          vinOrPA,
          {
            year: currentServiceDetails.year,
            make: currentServiceDetails.make,
            model: currentServiceDetails.model,
            validatedVin: currentServiceDetails?.validatedVin,
          },
          { isCompatible: false, isError: false, errorReason: null, isWarning: true, warningReason: errorReason }
        );
    }
  };

  // Refactored handleSaveService function
  const handleSaveService = () => {
    if (!isSaveServiceDisabled) setIsSaveServiceDisabled(true);

    const isVinChanged = currentServiceDetails?.validatedVin !== vin.value;
    const isBoxNameTypeCompatible = selectedVinOrPA.isBoxNameTypeCompatible;

    if (serviceType === SERVICE_TYPE_VIN && selectedVinOrPA.isVin) {
      saveServiceDetails(selectedVinOrPA, currentServiceDetails, BOXNAME_TYPE_MISMATCH_ERROR, isVinChanged, isBoxNameTypeCompatible);
    } else {
      saveServiceHelper(
        selectedVinOrPA,
        { year: null, make: null, model: null, validatedVin: "" },
        { isCompatible: false, isError: false, errorReason: null, isWarning: false, warningReason: null }
      );
    }
  };

  //function to validate the vin
  const handleVinValidation = (isEdit = false) => {
    const callBack = (year, make, model, isVinValid, apiFailed = false) => {
      if (apiFailed) {
        setIsServiceFailure((prev) => ({
          ...prev,
          vinValidationFailed: true,
        }));
        saveService(selectedVinOrPA, null, null, null, "", false, true, "Vin Validation API failed", false, null, isEdit);
      } else {
        isServiceFailure.vinValidationFailed &&
          setIsServiceFailure((prev) => ({
            ...prev,
            vinValidationFailed: false,
          }));
        if (isVinValid) {
          handleVinCompatibility(year, make, model, false, isEdit);
        } else {
          setVin((prev) => ({ ...prev, isValid: false, errorText: "VIN is invalid" }));
          dispatch(setLoader(false));
          saveService(selectedVinOrPA, null, null, null, vin.value, false, true, "Invalid vin", false, null, isEdit);
        }
      }
    };
    dispatch(validateVin(vin.value, callBack));
  };

  //function to check th compatibility of two vins
  const handleVinCompatibility = (year, make, model, toLoad = false, isEdit = false) => {
    const callBack = (isCompatible, notCompatibleReason, apiFailed = false) => {
      if (apiFailed) {
        setIsServiceFailure((prev) => ({
          ...prev,
          hwsCompatibilityFailed: true,
        }));
        saveService(selectedVinOrPA, year, make, model, vin.value, false, true, "Vin compatibility API failed", false, null, isEdit);
      } else {
        isServiceFailure.hwsCompatibilityFailed &&
          setIsServiceFailure((prev) => ({
            ...prev,
            hwsCompatibilityFailed: false,
          }));
        if (isCompatible) {
          saveService(selectedVinOrPA, year, make, model, vin.value, true, false, null, false, null, isEdit);
        } else {
          saveService(selectedVinOrPA, year, make, model, vin.value, false, false, null, true, notCompatibleReason, isEdit);
        }
      }
    };
    const camerasAndItsAccessories = selectedVinOrPA.cameras.length ? [...selectedVinOrPA.cameras.map((camera) => camera.name)] : [];
    dispatch(
      checkVinCompatibility(
        selectedVinOrPA.vin || selectedVinOrPA.newVin,
        vin.value,
        [...selectedAccessories, ...camerasAndItsAccessories],
        selectedVinOrPA.boxName,
        callBack,
        toLoad
      )
    );
  };

  const saveService = (
    selectedVehicleOrPA,
    year,
    make,
    model,
    validatedVin,
    isCompatible,
    isError,
    errorReason,
    isWarning,
    warningReason,
    IsEditNewlyAddedService = false
  ) => {
    let serviceDetailsList = [...serviceDetails];
    const service = createServiceObject(
      selectedVehicleOrPA,
      year,
      make,
      model,
      validatedVin,
      isCompatible,
      isError,
      errorReason,
      isWarning,
      warningReason
    );

    if (isEditVehicleAssetDetails.isOpen) {
      serviceDetailsList[isEditVehicleAssetDetails.index] = service;
      if (jobId) {
        updateExistingService(service, serviceDetailsList);
      }
    } else {
      if (jobId) {
        addNewServiceToPayload(service);
      }
      if (IsEditNewlyAddedService) {
        serviceDetailsList[serviceDetailsList.length - 1] = service;
      } else {
        serviceDetailsList.push(service);
      }
    }

    updateUIState(serviceDetailsList, isError, isWarning, service, errorReason);
  };

  const createServiceObject = (
    selectedVehicleOrPA,
    year,
    make,
    model,
    validatedVin,
    isCompatible,
    isError,
    errorReason,
    isWarning,
    warningReason
  ) => {
    let selectedCameraAndAccessories = [];
    if (selectedAccessories) {
      if (selectedVehicleOrPA.cameras.length) {
        selectedCameraAndAccessories = [...selectedAccessories.filter((accessory) => accessory !== "No accessories connected"), "Camera"];
      } else {
        selectedCameraAndAccessories = [...selectedAccessories];
      }
    }

  return {
    serviceType: serviceType === 1 ? "Transfer" : "Removal",
    vin: vin.value,
    isVinInvalid: errorReason === "Invalid vin",
    newVehicle: newVehicle.value,
    newVehicleId: null,
    existingVehicleId: null,
    year,
    make,
    model,
    validatedVin,
    isCompatible,
    selectedVinOrPA: selectedVehicleOrPA,
    selectedAccessories,
    uiIndex: "",
    isError,
    errorReason,
    isWarning,
    warningReason,
    selectedCameraAndAccessories,
  };
  };

  const updateExistingService = (service, serviceDetailsList) => {
    const existingServiceTypeName = serviceDetails[isEditVehicleAssetDetails.index].uiIndex;
    service.uiIndex = existingServiceTypeName;
    const payload = updateJob.payload;
    const updatedServicesPayload = payload?.services;

    updatedServicesPayload?.forEach((item) => {
      if (item.type === service.serviceType.toLowerCase()) {
        item?.servicesDetails?.forEach((obj, index) => {
          if (obj?.uiIndex === existingServiceTypeName && (obj.isDeleted === false || obj.isDeleted === "0")) {
            const serviceUpdatedObj = serviceDetailsObject(service, item.type);
            serviceUpdatedObj.existingVehicle.id = obj?.existingVehicle?.id;
            if (serviceUpdatedObj.newVehicle) serviceUpdatedObj.newVehicle.id = obj?.newVehicle?.id ?? null;
            serviceUpdatedObj.isDeleted = obj.isDeleted;
            item.servicesDetails[index] = serviceUpdatedObj;
          }
        });
      }
    });

    payload.services = updatedServicesPayload;
    dispatch(setUpdateJob({ payload: payload, isUpdated: true }));
  };

  const addNewServiceToPayload = (service) => {
    const payload = updateJob.payload;
    const updatedServicesPayload = payload?.services;

    const isTypeAvailable = updatedServicesPayload.filter((item) => item.type === service.serviceType.toLowerCase())?.length === 0;
    if (isTypeAvailable) {
      updatedServicesPayload.push({
        type: service.serviceType.toLowerCase(),
        servicesDetails: [],
      });
    }

    updatedServicesPayload?.forEach((item) => {
      if (item.type === service.serviceType.toLowerCase()) {
        const serviceUpdatedObj = serviceDetailsObject(service, item.type);
        serviceUpdatedObj.uiIndex = `${service.serviceType}${item?.servicesDetails.length + 1}`;
        service.uiIndex = `${service.serviceType}${item?.servicesDetails.length + 1}`;
        serviceUpdatedObj.isDeleted = !!serviceUpdatedObj?.isDeleted;
        item?.servicesDetails.push(serviceUpdatedObj);
      }
    });

    payload.services = updatedServicesPayload;
    dispatch(setUpdateJob({ payload: payload, isUpdated: true }));
  };

  const updateUIState = (serviceDetailsList, isError, isWarning, service, errorReason) => {
    setIsNextDisabled(isError || serviceDetailsList.some((service) => service.isError));
    dispatch(setIsAssistanceRequired(isWarning || serviceDetailsList.some((service) => service.isWarning)));
    setCurrentServiceDetails(service);

    if (!(isError && errorReason !== "Invalid vin")) {
      dispatch(setServiceDetails(serviceDetailsList));
      setIsFieldsEdited(false);
      !isAddAnotherService && setIsAddAnotherService(true);
      !isAdditionalService && setIsAdditionalService(true);
      setShowSnackBar({
        isOpen: true,
        action: "saved",
      });
      stepperRef?.current?.focus();
    }
  };

  return (
      <Wrapper border="1px solid #D8DADA" borderRadius="5px" marginTop="1rem" marginBottom="1.438rem" padding="0px 24px 24px">
        <ServiceTypeContainer>
          <Wrapper display="flex" justifyContent="space-between" width="100%">
            <ServiceTypeWrapper close={serviceCount !== -1}>
              <h2 className="sr-only" tabIndex="-1">
                Service {serviceCount === -1 ? 1 : serviceCount + 1}: &nbsp;{serviceType === 1 ? "Transfer" : "Removal"}
              </h2>
              <TextWrapper
                fontFamily="neue-haas-grotesk-display"
                fontSize="2rem"
                fontWeight="700"
                lineHeight="2.5rem"
                aria-hidden="true"
                tabIndex="-1"
              >
                Service {serviceCount === -1 ? 1 : serviceCount + 1}: &nbsp;
              </TextWrapper>
              <TextWrapper
                fontFamily="neue-haas-grotesk-display"
                fontSize="2rem"
                fontWeight="700"
                lineHeight="2.5rem"
                aria-hidden="true"
                tabIndex="-1"
              >
                {serviceType === 1 ? "Transfer" : "Removal"}
              </TextWrapper>
            </ServiceTypeWrapper>
            {serviceCount !== -1 && (
              <CustomisedTertiaryButton
                role="button"
                aria-label="close"
                buttonWidth="auto"
                onClick={() => {
                  handleClose();
                }}
                id={getPendoId("additional-service-close")}
              >
                CLOSE
              </CustomisedTertiaryButton>
            )}
          </Wrapper>
        </ServiceTypeContainer>
        {currentServiceDetails?.isWarning && !blockingError.isError && (
          <CPNotification isOpen={true} notificationType="warning" closeButton={false} height="auto" margin="1.5rem 0rem 0rem 0rem">
            <TextWrapperSpan>{WARNING_REASON_DISPLAY_MESSAGES[currentServiceDetails.warningReason]}</TextWrapperSpan>
          </CPNotification>
        )}
        {sfdcAccountDetailsError || revealVehiclesError || isServiceFailure.vinFeaturesError || isServiceFailure.vinControlUnitsError ? (
          <TryAgain handleClick={handleLandingPageAPI} />
        ) : (
          <>
            <CurrentVehicleSearch
              selectedVinOrPA={selectedVinOrPA}
              setSelectedVinOrPA={setSelectedVinOrPA}
              searchHandler={searchHandler}
              setSelectedAccessories={setSelectedAccessories}
              setHighlightAccessories={setHighlightAccessories}
              getDeviceType={getDeviceType}
              isWarning={currentServiceDetails?.isWarning}
              isBlockingErrorOccured={blockingError.isError}
            />
            {blockingError.isError && BLOCKING_ERROR_NOTIFICATIONS[blockingError.errorReason]}
            {!blockingError.isError && selectedVinOrPA?.isVin && (
              <CamerasAndOtherAccessories
                key={selectedVinOrPA.label}
                selectedAccessories={selectedAccessories}
                setSelectedAccessories={setSelectedAccessories}
                setHighlightAccessories={setHighlightAccessories}
                highlightAccessories={highlightAccessories}
                selectedVinOrPA={selectedVinOrPA}
              />
            )}
            {serviceType === 1 && <Line type="xLight" inverted={false} style={{ marginTop: "2rem" }} />}
            <Wrapper display="flex" flexDirection="column" alignItems="flex-start">
              {serviceType === 1 && (
                <NewVehicleorAsset
                  vin={vin}
                  setVin={setVin}
                  newVehicle={newVehicle}
                  setNewVehicle={setNewVehicle}
                  showVin={selectedVinOrPA === null || selectedVinOrPA?.isVin}
                  checkAccessoriesSection={checkAccessoriesSection}
                  handleTryAgain={handleTryAgain}
                  isServiceFailure={isServiceFailure.vinValidationFailed || isServiceFailure.hwsCompatibilityFailed}
                  isBlockingError={blockingError.isError}
                  validatedInvalidVin={currentServiceDetails?.errorReason === "Invalid vin" ? currentServiceDetails?.validatedVin : null}
                />
              )}
              {serviceType === 2 && !isSaveServiceDisabled && (
                <Wrapper
                  fontWeight="400"
                  fontSize="0.875rem"
                  marginTop={blockingError.isError && serviceType === 2 && !selectedVinOrPA?.isVin ? "1rem" : "2rem"}
                >
                  <ContractAndCancellationsFAQWarning />
                </Wrapper>
              )}
              <SaveService isSaveServiceDisabled={isSaveServiceDisabled} onClickHandler={handleSaveService} />
            </Wrapper>
          </>
        )}
      </Wrapper>
  );
};

VehicleAssetDetails.propTypes = {
  serviceType: PropTypes.number.isRequired,
  setShowSnackBar: PropTypes.func.isRequired,
  isAdditionalService: PropTypes.bool.isRequired,
  setIsAdditionalService: PropTypes.func.isRequired,
  stepperRef: PropTypes.object,
  isEditVehicleAssetDetails: PropTypes.shape({
    isOpen: PropTypes.bool.isRequired,
    index: PropTypes.number.isRequired,
  }).isRequired,
  setIsEditVehicleAssetDetails: PropTypes.func.isRequired,
  setIsDiscardChange: PropTypes.func.isRequired,
  isNextDisabled: PropTypes.bool.isRequired,
  setIsNextDisabled: PropTypes.func.isRequired,
  setIsAddAnotherService: PropTypes.func.isRequired,
  isAddAnotherService: PropTypes.bool.isRequired,
  serviceCount: PropTypes.number.isRequired,
  jobId: PropTypes.string,
  isFieldsEdited: PropTypes.bool.isRequired,
  setIsFieldsEdited: PropTypes.func.isRequired,
  setIsFirstServiceEdited: PropTypes.func.isRequired,
};

export default VehicleAssetDetails;
